'use client';

import { useEffect, useRef, useState } from 'react';

interface UseRotatingPlaceholderOptions {
  /** Rotation runs only while true; flipping false hard-resets to staticText. */
  enabled: boolean;
  /** Example queries to cycle through (after staticText leads the sequence). */
  texts: string[];
  /** The default placeholder — also the SSR/first-paint value. */
  staticText: string;
  /** Delay before the first swap, so the default line gets read once. */
  firstDelayMs?: number;
  /** Time each example stays on screen. */
  intervalMs?: number;
  /** Fade-out duration before the text swaps (must match the CSS transition). */
  fadeMs?: number;
}

/**
 * Cycles the search bar's placeholder through concrete example queries —
 * the teaching that the old demo buttons tried to do with meta-descriptions
 * nobody read. Returns the current placeholder plus a `fadingOut` flag the
 * caller maps to a `::placeholder` opacity class.
 *
 * Safety properties:
 * - SSR/hydration: the initial value is always `staticText`; rotation only
 *   starts client-side after `firstDelayMs`.
 * - `enabled === false` resets synchronously — the instant a character is
 *   typed or voice recording starts, the plain placeholder is back.
 * - `prefers-reduced-motion` disables rotation entirely.
 */
export function useRotatingPlaceholder({
  enabled,
  texts,
  staticText,
  firstDelayMs = 2400,
  intervalMs = 3600,
  fadeMs = 240,
}: UseRotatingPlaceholderOptions): { placeholder: string; fadingOut: boolean } {
  // -1 = staticText; 0..n-1 = texts[i]. Sequence: static → texts, looping.
  const [index, setIndex] = useState(-1);
  const [fadingOut, setFadingOut] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const active = enabled && !reducedMotion && texts.length > 0;

  useEffect(() => {
    if (!active) {
      setIndex(-1);
      setFadingOut(false);
      return;
    }

    // setTimeout chain (not setInterval) so the fade-out → swap → fade-in
    // steps stay sequenced; every pending step is cleared on cleanup
    // (StrictMode double-mount safe).
    const schedule = (delay: number, step: () => void) => {
      timerRef.current = window.setTimeout(step, delay);
    };

    const advance = () => {
      setFadingOut(true);
      schedule(fadeMs, () => {
        setFadingOut(false);
        setIndex((current) => (current + 1 >= texts.length ? -1 : current + 1));
        schedule(intervalMs, advance);
      });
    };

    schedule(firstDelayMs, advance);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, texts, firstDelayMs, intervalMs, fadeMs]);

  return {
    placeholder: index >= 0 && index < texts.length ? texts[index] : staticText,
    fadingOut,
  };
}
